import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Trash2, Loader2, Layers, Palette, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Brand } from "../components/Brand";
import { LibraryGate } from "../components/LibraryGate";
import { ShopProductCard } from "../components/ShopProductCard";
import api, { lockLibrary } from "../lib/api";

const DuckCard = ({ duck, onDelete }) => (
  <li data-testid={`library-duck-${duck.id}`} className="rounded-3xl border border-border bg-card overflow-hidden group">
    <div className="relative aspect-square bg-[#f6f4ef]">
      <img src={duck.thumbnail} alt={duck.name} className="h-full w-full object-cover" />
      <button
        data-testid={`library-delete-${duck.id}`}
        onClick={() => onDelete(duck)}
        className="absolute top-2 right-2 rounded-full bg-background/90 p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:text-destructive"
        title="Remove from library"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
    <div className="p-3 space-y-2">
      <p className="font-semibold text-sm truncate" title={duck.name}>{duck.name}</p>
      <p className="text-xs text-muted-foreground font-mono2">
        {duck.faces ? `${Math.round(duck.faces / 1000)}k tris` : "—"} · {duck.height_mm ? `${Math.round(duck.height_mm)} mm` : "—"}
      </p>
      <div className="flex gap-1 flex-wrap">
        {(duck.palette || []).map((p) => (
          <span key={p.slot} title={`${p.name || p.hex}`} className="h-4 w-4 rounded-full border border-border" style={{ background: p.hex }} />
        ))}
      </div>
      {duck.tags?.outfit && <p className="text-xs text-muted-foreground truncate">{duck.tags.character ? `${duck.tags.character} · ` : ""}{duck.tags.outfit}</p>}
    </div>
  </li>
);

const Inventory = ({ inv }) => {
  if (!inv || !inv.ducks) return null;
  return (
    <section data-testid="library-inventory" className="rounded-3xl border border-border bg-card/60 p-5 space-y-4">
      <p className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest text-muted-foreground">
        <Palette className="h-4 w-4" /> Your spool inventory · {inv.spools.length} colors across {inv.ducks} ducks
      </p>
      <ul className="flex flex-wrap gap-2">
        {inv.spools.map((s) => (
          <li key={s.hex} data-testid={`inventory-spool-${s.hex.slice(1)}`} className="flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-xs">
            <span className="h-4 w-4 rounded-full border border-border" style={{ background: s.hex }} />
            <span className="font-semibold">{s.name || s.hex}</span>
            <span className="text-muted-foreground">×{s.count}</span>
          </li>
        ))}
      </ul>
      {inv.settings?.layer_height && (
        <p className="text-xs text-muted-foreground font-mono2">
          Most used: layer {inv.settings.layer_height} mm · supports {inv.settings.support_type || "—"} · {inv.settings.printer_settings_id || ""}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        New ducks snap their palette to these spools once the library has 5+ ducks. Choose Light, Standard or Rich style matching in Step 1.
      </p>
    </section>
  );
};

export default function LibraryPage() {
  const [unlocked, setUnlocked] = useState(null); // null = checking the cookie session
  const [ducks, setDucks] = useState([]);
  const [inv, setInv] = useState(null);
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const locked = () => setUnlocked(false);
    window.addEventListener("duckify-library-locked", locked);
    return () => window.removeEventListener("duckify-library-locked", locked);
  }, []);

  const load = useCallback(async () => {
    try {
      const [l, i] = await Promise.all([api.get("/library"), api.get("/library/inventory")]);
      setDucks(l.data);
      setInv(i.data);
      setUnlocked(true);
    } catch (e) {
      if (e?.response?.status === 401) setUnlocked(false);
      else toast.error("Could not load the library.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const lock = async () => {
    await lockLibrary().catch(() => {});
    setUnlocked(false);
    setDucks([]);
    setInv(null);
  };

  const uploadAll = async (files) => {
    const list = Array.from(files).filter((f) => f.name.toLowerCase().endsWith(".3mf"));
    if (!list.length) return toast.error("Please choose Bambu Studio .3mf files.");
    setQueue(list.map((f) => f.name));
    for (const f of list) {
      setCurrent(f.name);
      const fd = new FormData();
      fd.append("file", f);
      fd.append("name", f.name.replace(/\.3mf$/i, ""));
      try {
        const { data } = await api.post("/library/import", fd, { timeout: 300000 });
        setDucks((d) => [data, ...d]);
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || "import failed"}`);
      }
      setQueue((q) => q.filter((n) => n !== f.name));
    }
    setCurrent(null);
    toast.success("Library updated");
    load();
  };

  const remove = async (duck) => {
    await api.delete(`/library/${duck.id}`);
    setDucks((d) => d.filter((x) => x.id !== duck.id));
    load();
  };

  return (
    <div className="App min-h-screen bg-dots">
      <header className="h-20 border-b border-border/70 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto h-full px-4 sm:px-8 lg:px-12 flex items-center justify-between">
          <Link to="/" data-testid="library-logo-link" className="flex items-center gap-3 group"><Brand /></Link>
          <div className="flex items-center gap-2">
            {unlocked && (
              <Button data-testid="library-lock-btn" variant="ghost" className="rounded-full gap-2" onClick={lock}><Lock className="h-4 w-4" /><span className="hidden sm:inline">Lock</span></Button>
            )}
            <Link to="/"><Button data-testid="library-back-btn" variant="outline" className="rounded-full gap-2"><Sparkles className="h-4 w-4" /> Back to studio</Button></Link>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 sm:py-14 space-y-8">
        <div>
          <span className="text-xs font-mono2 uppercase tracking-widest text-accent font-semibold">Duck library</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-2">Teach the AI your house style</h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            Upload your printed Bambu Studio ducks (.3mf). We read the mesh, colors and print settings, render a thumbnail and tag each duck —
            then every new render is guided by your closest ducks and your palette snaps to spools you actually own.
          </p>
        </div>

        {unlocked === null ? (
          <div className="grid place-items-center h-[300px]" data-testid="library-checking">
            <Loader2 className="h-8 w-8 animate-spin text-accent" />
          </div>
        ) : !unlocked ? (
          <LibraryGate onGranted={load} />
        ) : (
          <>
        <div
          data-testid="library-dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); uploadAll(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          className="rounded-3xl border-2 border-dashed border-primary/60 bg-card/50 p-8 text-center cursor-pointer hover:bg-card transition-colors"
        >
          <input ref={inputRef} data-testid="library-file-input" type="file" accept=".3mf" multiple className="hidden" onChange={(e) => uploadAll(e.target.files)} />
          {current ? (
            <p data-testid="library-upload-status" className="flex items-center justify-center gap-2 font-semibold"><Loader2 className="h-5 w-5 animate-spin" /> Importing {current}… ({queue.length} left)</p>
          ) : (
            <p className="flex items-center justify-center gap-2 font-semibold"><Upload className="h-5 w-5" /> Drop .3mf ducks here (multi-select OK) or click to choose</p>
          )}
          <p className="text-xs text-muted-foreground mt-2">Files up to ~50 MB each · imported one at a time · ~10 s per duck</p>
        </div>

        <Inventory inv={inv} />
        <ShopProductCard />

        <p className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest text-muted-foreground"><Layers className="h-4 w-4" /> {ducks.length} ducks in the library</p>
        <ul data-testid="library-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {ducks.map((d) => <DuckCard key={d.id} duck={d} onDelete={remove} />)}
        </ul>
          </>
        )}
      </main>
    </div>
  );
}
